import { useEffect, useState } from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import CircularProgress from "@mui/joy/CircularProgress";
import { bookApi } from "../../api.js";
import CoverPresetBrowser from "./CoverPresetBrowser.jsx";
import { logEvent } from "../../logger";

// Сколько популярных шаблонов показываем инлайн (остальные — в браузере по «Ещё»).
const INLINE_COUNT = 2;

// Готовые шаблоны обложки — применяют переднюю И заднюю сторону сразу.
export default function CoverPresetGallery({ bookId, onApplied }) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);
  const [browserOpen, setBrowserOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    bookApi.getCoverPresets()
      .then((d) => { if (alive) setPresets(d || []); })
      .catch(() => { if (alive) setPresets([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const apply = async (preset) => {
    if (applying || !bookId) return;
    setApplying(preset.id);
    try {
      logEvent({ source: "ui", event: "editor.cover_preset.selected", book_id: bookId,
        message: "Выбрал шаблон обложки",
        payload: { preset_id: preset.id, preset_title: preset.title || "",
          stage: "selection", persisted: false } });
      const updated = await bookApi.applyCoverPreset(bookId, preset.id);
      onApplied?.(updated);
    } catch (e) {
      window.alert("Не удалось применить шаблон обложки. Попробуйте ещё раз.");
    } finally {
      setApplying(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size="sm" />
      </Box>
    );
  }
  if (!presets.length) return null; // нет пресетов — блок просто не показываем

  const inline = presets.slice(0, INLINE_COUNT);
  const hasMore = presets.length > INLINE_COUNT;

  return (
    <Box sx={{ width: "100%" }} onClick={(e) => e.stopPropagation()}>
      <Typography level="body-xs" sx={{
        fontWeight: 700, color: "text.tertiary",
        textTransform: "uppercase", letterSpacing: "0.08em", mb: 1,
      }}>
        Готовые шаблоны обложки
      </Typography>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {inline.map((p) => (
          <Box
            key={p.id}
            onClick={() => apply(p)}
            sx={{
              flexShrink: 0, width: 88, cursor: "pointer",
              borderRadius: "12px", overflow: "hidden",
              border: "2px solid var(--joy-palette-divider)",
              opacity: applying && applying !== p.id ? 0.5 : 1,
              transition: "all 0.15s",
              "&:hover": { borderColor: "primary.400" },
              "&:active": { transform: "scale(0.96)" },
            }}
          >
            <Box sx={{
              width: "100%", aspectRatio: "3/4",
              bgcolor: p.cover_bg_color || "#834A90",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {p.preview_url && (
                <Box component="img" src={p.preview_url} alt={p.title}
                  draggable={false}
                  sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              )}
            </Box>
            <Typography level="body-xs" sx={{
              fontSize: "10px", textAlign: "center", p: "4px 2px",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {applying === p.id ? "Применяем…" : p.title}
            </Typography>
          </Box>
        ))}

        {/* Карточка-кнопка «Ещё» — открывает браузер всех шаблонов */}
        {hasMore && (
          <Box
            onClick={() => setBrowserOpen(true)}
            sx={{
              flexShrink: 0, width: 88, cursor: "pointer",
              borderRadius: "12px", overflow: "hidden",
              border: "2px dashed var(--joy-palette-divider)",
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              aspectRatio: "3/4",
              color: "text.secondary",
              transition: "all 0.15s",
              "&:hover": { borderColor: "primary.400", color: "primary.500" },
              "&:active": { transform: "scale(0.96)" },
            }}
          >
            <Typography sx={{ fontSize: "22px", lineHeight: 1, fontWeight: 700 }}>+</Typography>
            <Typography level="body-xs" sx={{ fontSize: "11px", fontWeight: 600, mt: 0.5 }}>
              Ещё
            </Typography>
          </Box>
        )}
      </Box>

      <CoverPresetBrowser
        open={browserOpen}
        onClose={() => setBrowserOpen(false)}
        bookId={bookId}
        onApplied={(updated) => { onApplied?.(updated); setBrowserOpen(false); }}
      />
    </Box>
  );
}